// apps/client/src/state/slices/shop.ts (M1-T14) — keys start with "shop" (§8.7); flows are plain functions taking the act command.
// Each flow edits `you` optimistically and returns the undo the action client runs on a reject; the ack's `priv` (applied by the
// action client) then brings the real bells, items, loan and shop picks.
import type { CatalogId, RoofColor } from '@whiskerton/content';
import type { ItemId, PrivateState } from '@whiskerton/shared';
import type { CoreCommands } from '../../game/commands/core';
import type { ActResult } from '../../net/actions';
import { useGame } from '../store';
import type { SliceCreator } from '../types';

export interface ShopSlice { shopTab: 'buy' | 'sell'; shopSetTab(t: 'buy' | 'sell'): void; shopSellPicks: ItemId[]; shopToggleSell(id: ItemId): void; shopClearSell(): void }
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends ShopSlice {} }
export const slice: SliceCreator<ShopSlice> = (set, get) => ({
  shopTab: 'buy', shopSetTab: (shopTab) => set({ shopTab }),
  shopSellPicks: [],
  shopToggleSell: (id) => set({ shopSellPicks: get().shopSellPicks.includes(id) ? get().shopSellPicks.filter((x) => x !== id) : [...get().shopSellPicks, id] }),
  shopClearSell: () => set({ shopSellPicks: [] }),
});

export type Act = CoreCommands['act'];
/** Optimistically edits `you`, returning the undo that restores the previous object. */
function edit(fn: (y: PrivateState) => PrivateState): () => void {
  const prev = useGame.getState().you;
  if (prev) useGame.setState({ you: fn(prev) });
  return () => useGame.setState({ you: prev });
}
export function buyItem(act: Act, catalogId: CatalogId): Promise<ActResult<'shop.buy'>> {
  return act('shop.buy', { catalogId }, { optimistic: () => edit((y) => {
    const price = y.shop?.daily.find((d) => d.catalogId === catalogId)?.price ?? y.shop?.always.find((d) => d.catalogId === catalogId)?.price ?? 0;
    return { ...y, bells: y.bells - price, shop: y.shop && { ...y.shop, daily: y.shop.daily.map((d) => (d.catalogId === catalogId ? { ...d, bought: true } : d)) } };
  }) });
}
/** Both actions share the `res` shape `{ earned: number }` (§4.6), so callers read `r.earned` whichever one ran. */
export function sellItems(act: Act, ids: readonly ItemId[], where: 'shop' | 'dropbox'): Promise<ActResult<'shop.sell' | 'dropbox.sell'>> {
  const type: 'shop.sell' | 'dropbox.sell' = where === 'shop' ? 'shop.sell' : 'dropbox.sell';   // T = the union; ActResult<T> matches the return type
  return act(type, { items: [...ids] }, { optimistic: () => edit((y) => ({ ...y, items: y.items.filter((i) => !ids.includes(i.id)) })) });
}
export function payLoan(act: Act, amount: number): Promise<ActResult<'loan.pay'>> {
  return act('loan.pay', { amount }, { optimistic: () => edit((y) => ({ ...y, bells: y.bells - amount, loan: y.loan - amount })) });
}
/** Contract change 35: the tent → cottage step names one of the four kit roof colours; every later step sends null. */
export function requestUpgrade(act: Act, roof: RoofColor | null): Promise<ActResult<'house.upgrade'>> {
  return act('house.upgrade', { roof });
}
